import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import type { ColumnDef } from "@tanstack/react-table";
import { DataTable } from "../components/DataTable";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { TableSkeleton } from "../components/states";
import { useActivateRule, useDeleteRule, useRules, type Rule } from "../lib/rules";
import { RuleActionBadge } from "../components/RuleAction";
import { ruleConflictMessage, ruleDisplayName } from "../lib/rule-action";

function formatCondition(rule: Rule): string {
  if (!rule.condition) return "(alle Events)";
  const { field, operator, value } = rule.condition;
  if (operator === "exists") return `${field} exists`;
  return `${field} ${operator} ${value}`;
}

export function RulesPage() {
  // Deactivated rules stay listed (greyed out) so they can be switched back on.
  const { data, isLoading } = useRules({ includeInactive: true });
  const navigate = useNavigate();
  const del = useDeleteRule();
  const activate = useActivateRule();
  const [confirmId, setConfirmId] = useState<number | null>(null);

  const items = data?.items ?? [];
  const activeCount = items.filter((r) => r.active).length;

  const columns = useMemo<ColumnDef<Rule>[]>(
    () => [
      {
        id: "id",
        header: "ID",
        cell: ({ row }) => (
          <span className="font-mono text-xs text-text-tertiary">
            #{row.original.id}
          </span>
        ),
        size: 60,
      },
      {
        id: "name",
        header: "Regel",
        cell: ({ row }) => {
          const r = row.original;
          return (
            <span className={`inline-flex flex-wrap items-center gap-2 ${r.active ? "" : "opacity-50"}`}>
              <RuleActionBadge action={r.action} />
              <span className="text-sm font-medium text-text-primary">
                {ruleDisplayName(r)}
              </span>
              {r.builtin && (
                <span className="rounded-full bg-bg-tertiary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
                  mitgeliefert
                </span>
              )}
              {!r.active && (
                <span className="rounded-full bg-bg-tertiary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
                  ausgeschaltet
                </span>
              )}
            </span>
          );
        },
      },
      {
        id: "project",
        header: "Gilt für",
        cell: ({ row }) => {
          const r = row.original;
          if (r.is_global) {
            return (
              <span className="inline-flex items-center gap-1 rounded-full border border-accent bg-accent-subtle px-2 py-0.5 text-xs font-medium text-accent-text">
                Global
              </span>
            );
          }
          if (r.project_ids.length === 0) {
            return (
              <span className="text-xs text-text-tertiary">— keine —</span>
            );
          }
          if (r.project_ids.length === 1) {
            return (
              <Link
                to={`/projects/${r.project_ids[0]}/rules`}
                onClick={(e) => e.stopPropagation()}
                className="font-medium text-text-primary hover:text-text-link-hover"
              >
                {r.project_names[0]}
              </Link>
            );
          }
          const visible = r.project_names.slice(0, 2).join(", ");
          const more = r.project_names.length - 2;
          return (
            <span
              className="text-text-primary"
              title={r.project_names.join(", ")}
            >
              {visible}
              {more > 0 && (
                <span className="ml-1 text-text-tertiary">+{more}</span>
              )}
            </span>
          );
        },
      },
      {
        id: "event_type",
        header: "Event-Typ",
        cell: ({ row }) => (
          <span className="font-mono text-xs text-text-primary">
            {row.original.event_type}
          </span>
        ),
        size: 140,
      },
      {
        id: "condition",
        header: "Bedingung",
        cell: ({ row }) => (
          <code className="rounded-sm bg-bg-tertiary px-1.5 py-0.5 font-mono text-xs text-text-primary">
            {formatCondition(row.original)}
          </code>
        ),
      },
      {
        id: "created",
        header: "Erstellt",
        cell: ({ row }) => (
          <span className="text-xs text-text-secondary">
            {new Date(row.original.created_at).toLocaleDateString("de-DE")}
          </span>
        ),
        size: 110,
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Aktionen</span>,
        cell: ({ row }) =>
          row.original.active ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setConfirmId(row.original.id);
              }}
              aria-label={`Rule ${row.original.id} ausschalten`}
              title="Ausschalten"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-secondary hover:bg-danger-bg hover:text-danger"
            >
              <Trash2 size={14} />
            </button>
          ) : (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                activate.mutate(row.original.id, {
                  onSuccess: () => toast.success("Regel eingeschaltet"),
                  onError: (err) =>
                    toast.error(
                      ruleConflictMessage(err) ??
                        `Fehlgeschlagen: ${(err as Error).message}`,
                      { duration: 12000 },
                    ),
                });
              }}
              className="rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-xs text-text-primary hover:bg-bg-hover"
            >
              Einschalten
            </button>
          ),
        size: 90,
      },
    ],
    [activate],
  );

  return (
    <div className="px-6 py-8">
      <div className="mx-auto max-w-content space-y-6">
        <header className="flex items-baseline justify-between">
          <div>
            <p className="font-mono text-xs uppercase tracking-wider text-text-tertiary">
              Rules
            </p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-text-primary">
              Approval-Regeln
              <span className="ml-3 font-mono text-base font-normal tabular-nums text-text-secondary">
                {activeCount}
              </span>
            </h1>
            <p className="mt-1 text-sm text-text-secondary">
              Jede Regel hat eine Art: <strong>prüfen</strong> (der Fall wartet
              in der Approval-Queue), <strong>immer zulassen</strong> oder{" "}
              <strong>immer ablehnen</strong>. Treffen mehrere Regeln, gewinnt:
              ablehnen vor zulassen vor prüfen. Zwei mitgelieferte Regeln lehnen
              Käufe mit Wert 0 und Test-Kontakte ab; du kannst sie ausschalten.
            </p>
          </div>
          <Link
            to="/rules/new"
            className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-2 text-sm font-medium text-text-on-accent hover:bg-accent-hover"
          >
            <Plus size={14} /> Neue Regel
          </Link>
        </header>

        {isLoading ? (
          <TableSkeleton rows={3} />
        ) : (
          <DataTable
            data={items}
            columns={columns}
            onRowClick={(r) => navigate(`/rules/${r.id}`)}
            emptyState={
              <div className="rounded-md border border-dashed border-border-primary bg-bg-secondary px-4 py-12 text-center">
                <p className="text-sm font-medium text-text-primary">
                  Noch keine Regeln
                </p>
                <p className="mt-1 text-sm text-text-secondary">
                  Alle Events laufen direkt durch in BigQuery.
                </p>
                <Link
                  to="/rules/new"
                  className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-text-on-accent hover:bg-accent-hover"
                >
                  <Plus size={14} /> Erste Regel anlegen
                </Link>
              </div>
            }
          />
        )}
      </div>

      <ConfirmDialog
        open={confirmId !== null}
        onOpenChange={(o) => !o && setConfirmId(null)}
        title="Regel ausschalten?"
        variant="danger"
        confirmLabel="Ausschalten"
        description="Die Regel gilt ab sofort nicht mehr und kann in dieser Liste wieder eingeschaltet werden. Bestehende wartende Fälle bleiben unangetastet."
        onConfirm={() => {
          if (confirmId === null) return;
          del.mutate(confirmId, {
            onSuccess: () => toast.success("Regel ausgeschaltet"),
            onError: (err) =>
              toast.error(`Fehlgeschlagen: ${(err as Error).message}`),
          });
          setConfirmId(null);
        }}
      />
    </div>
  );
}
